import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import { forEach } from 'lodash';
import tailwind from 'dealer-website-components/dist/css/tailwind.config';
import WysiwygEditorVariables from '../../../../components/common/WysiwygEditorVariables';

const getColorRecursive = (item, result) => {
  const keys = Object.keys(item);
  forEach(keys, (key) => {
    const value = item[key];

    if (typeof value === 'object') {
      getColorRecursive(value, result);
    } else {
      result.push(value);
    }
  });
};

const getThemeFonts = () => {
  const fonts = [];
  if (tailwind) {
    const keys = tailwind.theme.fontFamily ? Object.keys(tailwind.theme.fontFamily) : [];
    forEach(keys, (key) => {
      const fontFamilies = tailwind.theme.fontFamily[key];
      forEach(fontFamilies, (font) => {
        fonts.push(font);
      });
    });
  }
  return fonts;
};

const getThemeFontSizes = () => {
  const sizes = [];
  if (tailwind) {
    const keys = tailwind.theme.extend.fontSize ? Object.keys(tailwind.theme.extend.fontSize) : [];
    forEach(keys, (key) => {
      try {
        const size = parseInt(key);
        if (Number.isInteger(size)) {
          sizes.push(size);
        }
      } catch (ex) {}
    });
  }
  return sizes;
};

const getThemeColors = () => {
  const colors = ['#FFFFFF', '#000000'];
  if (tailwind) {
    getColorRecursive(tailwind.theme.extend.colors, colors);
  }
  return colors;
};

const defaultBlockTypes = {
  H1: {
    fontSize: '30px',
    tag: 'h1',
    fontWeight: '700'
  },
  H2: {
    fontSize: '24px',
    tag: 'h2',
    fontWeight: '700'
  },
  H3: {
    fontSize: '20px',
    tag: 'h3',
    fontWeight: '600'
  },
  H4: {
    fontSize: '18px',
    tag: 'h4',
    fontWeight: '600'
  },
  H5: {
    fontSize: '16px',
    tag: 'h5',
    fontWeight: '600'
  },
  H6: {
    fontSize: '14px',
    tag: 'h6',
    fontWeight: '600'
  }
};

const getEditorBlockTypes = () => {
  // Use those from dealer-website-components or use default
  const blockTypes = tailwind?.theme?.extend?.editorBlockTypes ? tailwind?.theme?.extend?.editorBlockTypes : defaultBlockTypes;
  return ['Normal', ...Object.keys(blockTypes)];
};

const getEditorTagStylesFromTailwind = () => {
  const styles = {};
  // If they are defined in dealer-website-component tailwind config, use that.
  const blockTypes = tailwind?.theme?.extend?.editorBlockTypes ? tailwind?.theme?.extend?.editorBlockTypes : defaultBlockTypes;

  // In dealer-website-components in tailwind config in theme extend add this:
  // editorBlockTypes: {
  //   H1: {
  //     label: 'Heading 1',
  //       tag: 'h1',
  //       fontSize: '30px',
  //   },
  //   H2: {
  //     label: 'Heading 2',
  //       tag: 'h2',
  //       fontSize: '24px',
  //   },
  //   H3: {
  //     label: 'Heading 3',
  //       tag: 'h3',
  //       fontSize: '18px',
  //   },
  // }
  Object.entries(blockTypes).forEach(([key, value]) => {
    if (value.tag) {
      styles[value.tag] = {
        fontSize: value.fontSize || 'inherit',
        fontWeight: value.fontWeight || 'inherit',
        color: value.color || 'black'
      };
    }
  });

  return styles;
};

export const defaultToolbar = {
  options: ['inline', 'link', 'list', 'blockType', 'fontFamily', 'fontSize', 'colorPicker', 'textAlign', 'history'],
  inline: {
    options: ['bold', 'italic', 'underline', 'strikethrough'],
    bold: { className: 'test', style: { color: 'red' } }
  },
  list: {
    options: ['unordered', 'ordered']
  },
  blockType: {
    options: getEditorBlockTypes()
  },
  fontSize: {
    // options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 40, 48, 60, 72, 96],
    options: getThemeFontSizes()
  },
  colorPicker: {
    // colors: ['#FFFFFF', '#000000'],
    colors: getThemeColors()
  },
  fontFamily: {
    options: getThemeFonts()
  }
};

const DraftJsEditor = ({ value, onChange, className, readOnly, updateValueOnChange, showVariables }) => {
  const rawData = value && value !== '' ? convertFromRaw(value) : undefined;
  const [editorState, setEditorState] = React.useState(rawData ? EditorState.createWithContent(rawData) : EditorState.createEmpty());
  const [error, setError] = useState(undefined);

  const editorRef = useRef(null);

  const updateEditorState = (value) => {
    const rawData = value && value !== '' ? convertFromRaw(value) : undefined;
    setEditorState(rawData ? EditorState.createWithContent(rawData) : EditorState.createEmpty());
  };

  useEffect(() => {
    if (updateValueOnChange) {
      // ugly, but time issues
      updateEditorState(value);
    }
  }, [value, updateValueOnChange]);

  const hasMatchedCurlyBraces = (newRawData) => {
    if (!newRawData) return true;

    const blockText = newRawData?.blocks?.[0]?.text;
    if (!blockText) return true;

    let openBracesCount = (blockText.match(/{/g) || []).length;
    let closingBracesCount = (blockText.match(/}/g) || []).length;

    return openBracesCount === closingBracesCount;
  };

  const handleChange = () => {
    const raw = convertToRaw(editorState.getCurrentContent());

    const compareA = JSON.stringify(raw);
    const compareB = JSON.stringify(value);

    if (hasMatchedCurlyBraces(raw)) {
      setError(undefined);
      if (compareA !== compareB) {
        onChange(raw);
      }
    } else {
      setError('Number of opened and closed curly braces needs to match.');
    }
  };

  const onEditorStateChange = useCallback((editorState) => {
    setEditorState(editorState);
  }, []);

  const headingTagStyles = getEditorTagStylesFromTailwind();

  const renderInjectedEditorStyles = () => {
    return (
      <style>
        {Object.entries(headingTagStyles)
          .map(
            ([tag, styleObj]) =>
              `.rdw-editor-wrapper ${tag} {
                ${Object.entries(styleObj)
                  .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())}: ${v};`)
                  .join('\n')}
              }`
          )
          .join('\n')}
      </style>
    );
  };

  return (
    <div>
      {renderInjectedEditorStyles()}
      <div className={`${className} ${error ? 'builder-border-red-500 builder-bg-red-100' : ''}`} style={{ minHeight: 400 }}>
        <Editor
          editorState={editorState}
          ref={editorRef}
          editorStyle={{
            minHeight: 400,
            cursor: readOnly ? 'not-allowed' : 'text',
            padding: '5px 10px'
          }}
          onEditorStateChange={onEditorStateChange}
          readOnly={readOnly}
          onChange={handleChange}
          toolbar={defaultToolbar}
          stripPastedStyles={true}
        />
      </div>
      {error && <div className='builder-mt-1 builder-text-red-500'>{error}</div>}
      {showVariables && <WysiwygEditorVariables />}
    </div>
  );
};

DraftJsEditor.defaultProps = {
  className: 'builder-border builder-border-gray-300 bg-gray-100',
  readOnly: false,
  updateValueOnChange: false,
  showVariables: false
};

export default DraftJsEditor;
